import { useMemo, useState } from 'react';
import { useModal } from '@/components/modals/Modal';
import { useTranslation } from '@/hooks/useTranslation';
import { PlusIcon } from '@/components/icons';
import {
  ConsultingCompanyRoleEnum,
  useInvitationTokenForConsultingCompanyAndRoleQuery,
} from '@/graphql/generated';
import { Field, Label } from '@headlessui/react';
import { RadioGroup } from '@/components/form/RadioGroup';
import { CopyTextToClipboardButton } from '@/components/generic/CopyTextToClipboardButton';
import StringDropdown from '@/components/generic/dropdown/StringDropdown';
import { generatePath } from 'react-router';
import { AppRoutes } from '@/screens/AppRoutes';
import {
  getDateFromISOString,
  getTimeFromISOString,
} from '@/services/DateService';

const expirationOptions = () => {
  const { t } = useTranslation();

  return [
    { id: '1d', label: t('enum:duration.1d'), duration: 1 },
    { id: '1w', label: t('enum:duration.1w'), duration: 7 },
    { id: '1m', label: t('enum:duration.1m'), duration: 30 },
    { id: 'unlimited', label: t('enum:duration.unlimited'), duration: null },
  ];
};

const InvitationModal = ({
  consultingCompanyId,
}: {
  consultingCompanyId: string;
}) => {
  const [selectedRole, setSelectedRole] = useState(
    ConsultingCompanyRoleEnum.Administrator,
  );
  const [selectedExpiration, setSelectedExpiration] = useState<{
    id: string;
    label: string;
    duration: number | null;
  } | null>(expirationOptions()[0]);
  const { t, i18n } = useTranslation();

  const expirationDate = useMemo(() => {
    if (selectedExpiration?.duration) {
      return new Date(
        Date.now() + selectedExpiration.duration * 24 * 60 * 60 * 1000,
      );
    }
    return null;
  }, [selectedExpiration?.duration]);

  const { data, loading } = useInvitationTokenForConsultingCompanyAndRoleQuery({
    variables: {
      consultingCompanyId,
      role: selectedRole,
      expirationDate: expirationDate?.toISOString(),
    },
    skip: !consultingCompanyId,
  });

  const expirationMessage = useMemo(() => {
    if (expirationDate) {
      return t('project.users.invitationModal.expirationMessage', {
        date:
          getDateFromISOString(expirationDate.toISOString(), i18n.language) +
          '  ' +
          getTimeFromISOString(expirationDate.toISOString(), i18n.language),
      });
    }
  }, [expirationDate, t]);

  return (
    <div className="flex flex-col gap-4">
      <h6>{t('project.users.invitationModal.chooseRole')}</h6>

      <RadioGroup<ConsultingCompanyRoleEnum>
        value={selectedRole}
        onChange={setSelectedRole}
        options={Object.values(ConsultingCompanyRoleEnum).map((role) => ({
          label: t(`enum:consultingCompanyRole.${role}`),
          value: role,
        }))}
      ></RadioGroup>

      <div className="flex flex-col gap-2">
        <Field>
          <Label>{t('project.users.invitationModal.chooseExpiration')}</Label>
          <StringDropdown
            availableItems={expirationOptions()}
            item={selectedExpiration}
            setItem={setSelectedExpiration}
            itemFormatter={(item) => item.label}
          />
          <p className="sami-body-small text-gray-500 mt-2">{expirationMessage}</p>
        </Field>
      </div>

      <CopyTextToClipboardButton
        buttonLabel={t('project.users.invitationModal.copyButton')}
        textToCopy={
          data?.invitationTokenForConsultingCompanyAndRole.token
            ? `${window.location.origin}${generatePath(
                AppRoutes.RegisterConsultingCompany,
                {
                  companyId: consultingCompanyId,
                  token: data?.invitationTokenForConsultingCompanyAndRole.token,
                },
              )}`
            : '#'
        }
        className="purple small"
        disabled={
          loading || !data?.invitationTokenForConsultingCompanyAndRole.token
        }
      />
    </div>
  );
};

export const InvitationModalButton = ({
  consultingCompanyId,
}: {
  consultingCompanyId: string;
}) => {
  const modal = useModal();
  const { t } = useTranslation();

  const openInvitationModal = () => {
    modal.openModal(
      <InvitationModal consultingCompanyId={consultingCompanyId} />,
      {
        title: t('project.users.invitationModal.title'),
        isFullscreen: false,
        displayTitleBar: true,
      },
    );
  };

  return (
    <button
      className="primary small purple shrink-0"
      onClick={openInvitationModal}
    >
      <PlusIcon />
      <span>{t('project.users.invite.button')}</span>
    </button>
  );
};
